import { useCallback, useRef } from 'react';

import { BillingActions, CommonActions } from '@Actions/index';
import { IConsultationInvoice } from '@Reducers/billing';
import { BillType } from '@Reducers/checkout';
import { IQueryData } from '@Reducers/common';
import { IRx } from '@Reducers/encounter';
import { Selectables } from '@Reducers/selectables';
import { useAppDispatch, useAppSelector } from '@Redux/hooks';
import { ICreatePrescriptionInvoicePayload, InvoiceMode } from '@Services/Billing/billing-payload';
import { INSTANT_LOCATION_CODE } from '@Utils/constants';

interface IGeneratePreview {
  voucherCode?: string;
  rx?: IRx;
  rxInvoicePreview?: IConsultationInvoice;
  locationId?: string;
  consultationTypeId?: string;
  invoiceMode?: InvoiceMode;
  locationCode?: string;
  onSuccess?: () => void;
}

const useInvoice = () => {
  const dispatch = useAppDispatch();
  const queryData: IQueryData = useAppSelector((state) => state.common.queryData);
  const selectables: Selectables = useAppSelector((state) => state.selectables);
  const queryDataRef = useRef(null);
  queryDataRef.current = queryData;
  const nonInstantInvoicePreview: IConsultationInvoice =
    useAppSelector((state) =>
      state.billing.consultationInvoicePreviews?.find(
        (invoice) => invoice?.locationCode !== INSTANT_LOCATION_CODE || !invoice?.locationCode,
      ),
    ) || {};
  const instantInvoicePreview: IConsultationInvoice =
    useAppSelector((state) =>
      state.billing.consultationInvoicePreviews?.find((invoice) => invoice?.locationCode === INSTANT_LOCATION_CODE),
    ) || {};

  const generatePreview = useCallback(
    ({
      voucherCode,
      rx,
      locationId,
      consultationTypeId,
      invoiceMode,
      rxInvoicePreview,
      onSuccess,
      locationCode,
    }: IGeneratePreview) => {
      dispatch(
        CommonActions.UpdateQueryData({
          voucher_code: voucherCode ?? '',
        }),
      );
      if (!rx) {
        if (consultationTypeId && locationId) {
          dispatch(
            BillingActions.PreviewConsultationInvoice({
              typeId: consultationTypeId,
              locationId,
              mode: invoiceMode,
              voucherCode: voucherCode,
              locationCode: locationCode,
              providerIds: queryDataRef.current?.providerId,
              consultationCode: queryDataRef.current?.consultationCode,
              attributes: {
                surchargeRate: queryDataRef.current?.surcharge,
                isInstant: selectables?.isInstant || queryDataRef.current?.locationCode === INSTANT_LOCATION_CODE,
              },
              ignoreDuplicateConsultation: true,
            }),
          );
        }
        return;
      }
      if (rx.billType === BillType.PAID || !rxInvoicePreview?.encounterId) return;
      const voucherPayload: ICreatePrescriptionInvoicePayload = {
        encounterId: rxInvoicePreview?.encounterId,
        ...(voucherCode && {
          voucherCode: voucherCode,
        }),
        mode: InvoiceMode.Prepaid,
        version: 1,
      };
      dispatch(BillingActions.GetInvoiceOverview(voucherPayload)).then(() => {
        onSuccess?.();
      });
    },
    [dispatch],
  );
  return { nonInstantInvoicePreview, instantInvoicePreview, generatePreview };
};

export default useInvoice;
